"use client";

import type { CSSProperties, ElementType } from "react";
import { motion, useReducedMotion } from "framer-motion";

import IconBell from "@/app/assets/Icons/Bell.svg";
import IconCalendar from "@/app/assets/Icons/Calendar.svg";
import IconChat from "@/app/assets/Icons/Chat.svg";
import IconFlag from "@/app/assets/Icons/Flag.svg";
import IconFolder from "@/app/assets/Icons/Folder.svg";
import IconLightBulb from "@/app/assets/Icons/LightBulb.svg";
import IconMail from "@/app/assets/Icons/Mail.svg";
import IconNote from "@/app/assets/Icons/Note.svg";
import IconProjects from "@/app/assets/Icons/Projects.svg";
import IconStar from "@/app/assets/Icons/Star.svg";
import IconTask from "@/app/assets/Icons/Task.svg";

/** Slow wandering loops (px), one per element, so no two drift in step. */
const JIGGLE = [
  { x: [0, 7, -5, 4, -6, 0], y: [0, -5, 7, -4, 6, 0], dur: 7.2 },
  { x: [0, -6, 5, -7, 4, 0], y: [0, 6, -5, 7, -4, 0], dur: 8.8 },
  { x: [0, 5, -7, 3, -5, 0], y: [0, -7, 5, -6, 4, 0], dur: 7.6 },
  { x: [0, -4, 6, -5, 7, 0], y: [0, 5, -6, 4, -7, 0], dur: 9.5 },
  { x: [0, 6, -4, 7, -3, 0], y: [0, -4, 6, -5, 7, 0], dur: 6.8 },
  { x: [0, -7, 4, -5, 6, 0], y: [0, 4, -7, 5, -6, 0], dur: 10.3 },
  { x: [0, 4, -6, 7, -5, 0], y: [0, -6, 3, -7, 5, 0], dur: 8.1 },
];

/**
 * Half-width (px from the centre line) of the hero content, by height from the top of the
 * section, at `xl` and up where the layout no longer changes: headline, sub-line, buttons
 * and offer line, then the film card (max-w-5xl) and the store links under it.
 */
const CONTENT_BANDS = [
  { until: 400, half: 424 },
  { until: 500, half: 336 },
  { until: 650, half: 297 },
  { until: 1340, half: 520 },
];
/**
 * The film card's band is the last one. Its half-width grows with the film from 1920 px
 * (max-w-6xl, then max-w-7xl from 2200 px, see HeroSection), so it is read from the
 * `--hero-film-half` custom property set on the layer rather than baked in.
 */
const FILM_BAND = CONTENT_BANDS.length - 1;
/** Clearance kept between an item and the content, and between an item and the viewport edge. */
const PAD = 20;
/** Widest spread (px) of the side lanes, so items stay grouped around the content on wide screens. */
const MAX_SPREAD = 400;
/** Icon box size (px). */
const ICON = 48;

type Base = {
  /** Which side of the content. */
  side: "l" | "r";
  /** Top (px) from the top of the hero section. */
  y: number;
  /** Position across the free lane at that height: 0 hugs the content, 1 hugs the viewport edge. */
  f: number;
  /** Drift loop index in {@link JIGGLE}. */
  j: number;
  /** Only shown from `2xl` (1536 px): the lanes are too narrow for it at 1280. */
  wide?: boolean;
};
type Item = Base & ({ kind: "icon"; Icon: ElementType } | { kind: "star" | "dot"; size: number });

/**
 * The original hero's floating set, spread down the whole hero (headline to the film card)
 * like the first landing: few items per screen, big gaps, sides and heights alternating.
 */
const ITEMS: Item[] = [
  { side: "l", kind: "star", size: 25, y: 175, f: 0.1, j: 3 },
  { side: "l", kind: "icon", Icon: IconCalendar, y: 265, f: 0.55, j: 0 },
  { side: "l", kind: "dot", size: 12, y: 395, f: 0.85, j: 0 },
  { side: "l", kind: "icon", Icon: IconBell, y: 500, f: 0.35, j: 1 },
  { side: "l", kind: "star", size: 18, y: 620, f: 0.75, j: 5 },
  { side: "l", kind: "icon", Icon: IconFolder, y: 735, f: 0.5, j: 0 },
  { side: "l", kind: "dot", size: 20, y: 862, f: 0.15, j: 6, wide: true },
  { side: "l", kind: "icon", Icon: IconNote, y: 975, f: 0.25, j: 2 },
  { side: "l", kind: "star", size: 20, y: 1088, f: 0.8, j: 6, wide: true },
  { side: "l", kind: "icon", Icon: IconChat, y: 1195, f: 0.6, j: 2 },

  { side: "r", kind: "dot", size: 20, y: 185, f: 0.3, j: 4 },
  { side: "r", kind: "icon", Icon: IconTask, y: 300, f: 0.4, j: 5 },
  { side: "r", kind: "star", size: 12, y: 440, f: 0.75, j: 2 },
  { side: "r", kind: "icon", Icon: IconMail, y: 560, f: 0.6, j: 6 },
  { side: "r", kind: "dot", size: 15, y: 705, f: 0.15, j: 4 },
  { side: "r", kind: "icon", Icon: IconFlag, y: 820, f: 0.3, j: 1 },
  { side: "r", kind: "star", size: 17, y: 935, f: 0.85, j: 6, wide: true },
  { side: "r", kind: "icon", Icon: IconProjects, y: 1040, f: 0.6, j: 4 },
  { side: "r", kind: "dot", size: 18, y: 1140, f: 0.1, j: 3, wide: true },
  { side: "r", kind: "icon", Icon: IconLightBulb, y: 1225, f: 0.6, j: 3 },
];

/**
 * CSS position of an item: its top, and a `left` computed from the centre line so it stays in
 * the free lane beside the content at that height, whatever the viewport width.
 */
function place(it: Item): CSSProperties {
  const size = it.kind === "icon" ? ICON : it.size;
  const overlapping = CONTENT_BANDS.map((b, i) => ({ ...b, i })).filter(
    (b) => it.y - PAD < b.until && it.y + size + PAD > (CONTENT_BANDS[b.i - 1]?.until ?? 0),
  );
  // The film is the widest content: any item level with it keeps clear of its live width.
  const half = overlapping.some((b) => b.i === FILM_BAND)
    ? "var(--hero-film-half)"
    : `${Math.max(...overlapping.map((b) => b.half))}px`;
  const near = `(${half} + ${PAD}px)`;
  const lane = `max(0px, min(50% - ${near} - ${PAD + size}px, ${MAX_SPREAD}px))`;
  const left =
    it.side === "l" ? `calc(50% - ${near} - ${size}px - ${it.f} * ${lane})` : `calc(50% + ${near} + ${it.f} * ${lane})`;
  return { top: it.y, left };
}

/**
 * Decorative layer of the home hero: product icons, sparkles and dots scattered down both
 * sides of the whole section (headline to film), drifting slowly, the icons wiggling when
 * hovered. Only from `xl` up (below, the sub-line and the buttons reach the sides), with a
 * lighter set until `2xl`; still under reduced motion.
 */
export function HeroDecor() {
  const reduced = useReducedMotion();
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden select-none [--hero-film-half:520px] xl:block min-[1920px]:[--hero-film-half:576px] min-[2200px]:[--hero-film-half:640px]"
    >
      {ITEMS.map((it, k) => {
        const { x, y, dur } = JIGGLE[it.j];
        const drift = reduced
          ? {}
          : { animate: { x, y }, transition: { duration: dur, repeat: Infinity, ease: "easeInOut" as const } };
        const cls = `absolute ${it.wide ? "hidden 2xl:block" : ""}`;
        if (it.kind === "icon") {
          const { Icon } = it;
          return (
            <motion.div key={k} {...drift} className={cls} style={place(it)}>
              <motion.div
                whileHover={reduced ? undefined : { scale: 1.4, rotate: [0, -8, 8, 0] }}
                transition={{ duration: 0.35 }}
                className="pointer-events-auto"
              >
                <Icon width={ICON} height={ICON} style={{ display: "block", overflow: "visible" }} />
              </motion.div>
            </motion.div>
          );
        }
        return (
          <motion.div key={k} {...drift} className={cls} style={{ ...place(it), width: it.size, height: it.size }}>
            {it.kind === "star" ? (
              <IconStar width={it.size} height={it.size} viewBox="0 0 24 24" style={{ display: "block", overflow: "visible" }} />
            ) : (
              <svg width={it.size} height={it.size} viewBox="0 0 24 24" className="block text-blue-light">
                <circle cx="12" cy="12" r="10" fill="currentColor" />
              </svg>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
